import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Edit, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import RichEditor from '../../components/admin/RichEditor';
import './AdminPageForm.css';
import api from '../../utils/api';
import './AdminNews.css';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

// ── List ─────────────────────────────────────────────────
export function AdminNewsList() {
  const [news, setNews]   = useState([]);
  const [total, setTotal] = useState(0);
  const [page,  setPage]  = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 15 });
    if (status) p.set('status', status);
    api.get(`/admin/news?${p}`)
      .then(r => { setNews(r.data.data); setTotal(r.data.total); setPages(r.data.pages); })
      .finally(() => setLoading(false));
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`A jeni i sigurt që doni të fshini "${title}"?`)) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success('Lajmi u fshi.');
      load();
    } catch { toast.error('Gabim gjatë fshirjes.'); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Lajmet</h1>
          <p className="admin-page-sub">{total} lajme gjithsej</p>
        </div>
        <Link to="/admin/lajmet/i-ri" className="btn btn-primary">
          <Plus size={15}/> Lajm i ri
        </Link>
      </div>

      <div className="card">
        <div className="admin-toolbar">
          {['', 'published', 'draft', 'archived'].map(s => (
            <button
              key={s}
              className={`tab-btn ${status === s ? 'active' : ''}`}
              onClick={() => { setStatus(s); setPage(1); }}
            >
              {s === '' ? 'Të gjitha' : s === 'published' ? 'Publikuar' : s === 'draft' ? 'Draft' : 'Arkivuar'}
            </button>
          ))}
        </div>

        {loading ? <div className="spinner" /> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Titulli</th>
                  <th>Kategoria</th>
                  <th>Statusi</th>
                  <th>Shikimet</th>
                  <th>Data</th>
                  <th>Veprimet</th>
                </tr>
              </thead>
              <tbody>
                {news.map(n => (
                  <tr key={n.id}>
                    <td className="td-title-cell">
                      <span className="td-main-title">{n.title}</span>
                      {n.is_featured ? <span className="badge featured-badge">⭐ E veçuar</span> : null}
                    </td>
                    <td><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{n.category_name || '—'}</span></td>
                    <td>
                      <span className={`badge status-${n.status}`}>
                        {n.status === 'published' ? 'Publikuar' : n.status === 'draft' ? 'Draft' : 'Arkivuar'}
                      </span>
                    </td>
                    <td><span style={{fontSize:'0.8rem'}}>{n.views}</span></td>
                    <td><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>
                      {new Date(n.created_at).toLocaleDateString('sq-AL')}
                    </span></td>
                    <td>
                      <div className="action-btns">
                        {n.status === 'published' && (
                          <a href={`/lajmet/${n.slug}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                            <Eye size={13}/>
                          </a>
                        )}
                        <Link to={`/admin/lajmet/${n.id}/ndrysho`} className="btn btn-secondary btn-sm">
                          <Edit size={13}/>
                        </Link>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(n.id, n.title)}>
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan={6} style={{textAlign:'center',color:'var(--gray-400)',padding:'2rem'}}>Nuk ka lajme.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="pagination" style={{padding:'1rem'}}>
            {Array.from({length: pages},(_,i)=>i+1).map(p => (
              <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={()=>setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ── Form (Create + Edit) ─────────────────────────────────
export function AdminNewsForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: '', excerpt: '', content: '',
    category_id: '', status: 'draft', is_featured: false,
    published_at: new Date().toISOString().slice(0, 16),
  });
  const [coverFile, setCoverFile]       = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [existingCover, setExistingCover] = useState('');
  const [loading, setLoading]  = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  useEffect(() => {
    api.get('/news-categories').then(r => setCategories(r.data));
    if (isEdit) {
      api.get(`/admin/news/${id}`)
        .then(r => {
          const found = r.data;
          setForm({
            title:       found.title       || '',
            excerpt:     found.excerpt     || '',
            content:     found.content     || '',
            category_id: found.category_id || '',
            status:      found.status      || 'draft',
            is_featured: Boolean(found.is_featured),
            published_at: found.published_at
              ? new Date(found.published_at).toISOString().slice(0, 16)
              : new Date().toISOString().slice(0, 16),
          });
          if (found.cover_image) {
            setCoverPreview(`${IMG_BASE}${found.cover_image}`);
            setExistingCover(found.cover_image);
          }
        })
        .catch(() => toast.error('Gabim gjatë ngarkimit të lajmit.'))
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Titulli është i detyrueshëm.');
    setLoading(true);

    const fd = new FormData();
    fd.append('title',       form.title);
    fd.append('excerpt',     form.excerpt      || '');
    fd.append('content',     form.content      || '');
    fd.append('category_id', form.category_id  || '');
    fd.append('status',      form.status       || 'draft');
    fd.append('is_featured',   form.is_featured  ? '1' : '0');
    fd.append('published_at', form.published_at || new Date().toISOString());
    if (coverFile) {
      fd.append('cover_image', coverFile);
    } else if (existingCover) {
      fd.append('existing_cover', existingCover);
    }

    try {
      if (isEdit) {
        await api.put(`/admin/news/${id}`, fd);
        toast.success('Lajmi u përditësua.');
      } else {
        await api.post('/admin/news', fd);
        toast.success('Lajmi u krijua.');
      }
      navigate('/admin/lajmet');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gabim.');
    } finally {
      setLoading(false);
    }
  };

      if (fetching) return <AdminLayout><div className="spinner"/></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">{isEdit ? 'Ndrysho Lajmin' : 'Lajm i Ri'}</h1>
        </div>
        <Link to="/admin/lajmet" className="btn btn-secondary">← Kthehu</Link>
      </div>

      <form onSubmit={handleSubmit} className="news-form-grid">
        <div className="news-form-main">
          <div className="card" style={{padding:'1.25rem'}}>
            <div className="form-group">
              <label className="form-label">Titulli *</label>
              <input className="form-input" required placeholder="Shkruaj titullin..."
                value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Nënshkrimi (excerpt)</label>
              <textarea className="form-input" rows={3} placeholder="Përshkrim i shkurtër..."
                value={form.excerpt} onChange={e => setForm(p=>({...p,excerpt:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Përmbajtja *</label>
              <div className="card" style={{overflow:'hidden',border:'1px solid #dddbd6'}}>
                <RichEditor
                  value={form.content}
                  onChange={content => setForm(p => ({...p, content}))}
                  placeholder="Shkruani përmbajtjen e lajmit..."
                />
              </div>
            </div>
          </div>  {/* end card */}
        </div>  {/* end news-form-main */}

        <div className="news-form-side">
          {/* Publish */}
          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Publikimi</h3>
            <div className="form-group">
              <label className="form-label">Statusi</label>
              <select className="form-input form-select" value={form.status}
                onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                <option value="draft">Draft</option>
                <option value="published">Publiko tani</option>
                <option value="archived">Arkivo</option>
              </select>
            </div>
            <div className="form-group" style={{marginTop:'0.75rem'}}>
              <label className="form-label">Data e publikimit</label>
              <input
                type="datetime-local"
                className="form-input"
                value={form.published_at}
                onChange={e => setForm(p => ({...p, published_at: e.target.value}))}
              />
            </div>
            <label className="form-label" style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
              <input type="checkbox" checked={form.is_featured}
                onChange={e => setForm(p=>({...p,is_featured:e.target.checked}))}/>
              E veçuar (featured)
            </label>
            <div style={{marginTop:'1rem',display:'flex',gap:8}}>
              <button type="submit" className="btn btn-primary" style={{flex:1,justifyContent:'center'}} disabled={loading}>
                {loading ? 'Duke ruajtur...' : isEdit ? 'Ruaj ndryshimet' : 'Krijo lajmin'}
              </button>
            </div>
          </div>

          {/* Category */}
          <div className="card" style={{padding:'1.25rem',marginBottom:'1rem'}}>
            <h3 className="form-side-title">Kategoria</h3>
            <select className="form-input form-select" value={form.category_id}
              onChange={e => setForm(p=>({...p,category_id:e.target.value}))}>
              <option value="">Pa kategori</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Cover image */}
          <div className="card" style={{padding:'1.25rem'}}>
            <h3 className="form-side-title">Foto kryesore</h3>
            {coverPreview && (
              <img src={coverPreview} alt="cover" style={{width:'100%',borderRadius:8,marginBottom:10,objectFit:'cover',maxHeight:160}}/>
            )}
            <input type="file" accept="image/*" onChange={handleFile} className="form-input"/>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
